import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVerticalIcon } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Modal, Button } from '@noor/ui';
import { toast, type ModalRenderProps } from '@noor/lib';
import { Can } from '@/entities/access';
import { useStatusPriority } from '../model/use-status-priority';
import { buildStageList, type PriorityStageItem } from '../lib/build-stage-list';

/**
 * Status-priority reorder dialog (dnd-kit). Ported from cpanel
 * `StatusPriorityDialog`. Self-contained: reads the effective order from the
 * shared hook, lets the user drag-reorder the 4 priority stages, and saves back.
 * On the CURRENT tab this drives the `status_id_order` list sort.
 */
export function StatusPriorityModal({ close }: ModalRenderProps<'status-priority'>) {
  const { t } = useTranslation('orders');
  const { effectiveOrder, save, reset } = useStatusPriority();
  const [draft, setDraft] = useState<PriorityStageItem[]>(() =>
    buildStageList(effectiveOrder),
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((items) => {
      const oldIndex = items.findIndex((i) => i.number === active.id);
      const newIndex = items.findIndex((i) => i.number === over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const onSave = () => {
    save(draft.map((s) => s.number));
    toast.success(t('priority.savedSuccess'));
    close();
  };

  const onReset = () => {
    reset();
    close();
  };

  return (
    <Can permission="orders.priority.manage">
      <Modal
        title={t('priority.title')}
        description={t('priority.description')}
        footer={
          <div className="flex w-full items-center justify-between">
            <Button variant="soft" shape="rounded" size="modal" onClick={onReset}>
              {t('priority.reset')}
            </Button>
            <div className="flex gap-3">
              <Button variant="soft" size="modal" shape="rounded" onClick={close}>
                {t('confirm.cancel')}
              </Button>
              <Button variant="dark" size="modal" shape="rounded" onClick={onSave}>
                {t('priority.save')}
              </Button>
            </div>
          </div>
        }
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={draft.map((s) => s.number)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-2">
              {draft.map((stage) => (
                <SortableRow key={stage.number} stage={stage} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      </Modal>
    </Can>
  );
}

function SortableRow({ stage }: { stage: PriorityStageItem }) {
  const { t } = useTranslation('orders');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: stage.number,
    });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex cursor-grab items-center gap-3 rounded-lg border px-3 py-2 ${
        isDragging ? 'bg-accent' : 'bg-card'
      }`}
      {...attributes}
      {...listeners}
    >
      <GripVerticalIcon className="size-4 text-muted-foreground" />
      <span
        className="size-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: stage.color }}
        aria-hidden
      />
      <span className="text-sm">{t(stage.i18nKey, stage.name)}</span>
    </li>
  );
}
